import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { 
  Truck, Package, MapPin, Clock, CheckCircle, AlertCircle, 
  ScanBarcode, ArrowRight, RefreshCw, Store, Calendar,
  DollarSign, User, ChevronRight, Filter, Search, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DriverFlowPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  
  // Data states
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [completedToday, setCompletedToday] = useState([]);
  const [pickingLists, setPickingLists] = useState([]);
  const [stats, setStats] = useState({
    pendingCount: 0,
    completedToday: 0,
    totalValueToday: 0,
    routePickups: 0,
    storeSales: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPendingDeliveries(),
        fetchCompletedToday(),
        fetchPickingLists(),
        fetchStats()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Data uppdaterad");
  };

  const fetchPendingDeliveries = async () => {
    try {
      // Get active picking lists with items that need to be picked
      const res = await axios.get(`${API}/picking/picking-lists/active`);
      const lists = res.data || [];
      
      // Extract pending items from all lists
      const pending = [];
      for (const list of lists) {
        const unpickedItems = (list.items || []).filter(item => !item.picked && !item.missing);
        for (const item of unpickedItems) {
          pending.push({
            ...item,
            listId: list.id,
            listName: list.name,
            transportor: item.transportor || list.transportor || ""
          });
        }
      }
      setPendingDeliveries(pending);
    } catch (error) {
      console.error("Error fetching pending:", error);
      setPendingDeliveries([]);
    }
  };

  const fetchCompletedToday = async () => {
    try {
      // Use server-side date filtering
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const res = await axios.get(`${API}/history/logs?action_type=OUT&date_from=${today.toISOString()}&limit=100`);
      const logs = res.data?.logs || [];
      setCompletedToday(logs);
    } catch (error) {
      console.error("Error fetching completed:", error);
      setCompletedToday([]);
    }
  };

  const fetchPickingLists = async () => {
    try {
      const res = await axios.get(`${API}/picking/picking-lists/active`);
      setPickingLists(res.data || []);
    } catch (error) {
      console.error("Error fetching picking lists:", error);
      setPickingLists([]);
    }
  };

  const fetchStats = async () => {
    try {
      // Use server-side date filtering for better performance
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const res = await axios.get(`${API}/history/logs?action_type=OUT&date_from=${today.toISOString()}&limit=200`);
      const todayLogs = res.data?.logs || [];
      
      // Get sales prices for value calculation
      const eans = [...new Set(todayLogs.map(l => l.ean))];
      let totalValue = 0;
      
      if (eans.length > 0) {
        try {
          const pricesRes = await axios.get(`${API}/picking/sales-prices/bulk/${eans.join(',')}`);
          const prices = pricesRes.data?.prices || {};
          
          todayLogs.forEach(log => {
            const price = prices[log.ean]?.avg_price_excl_vat || 0;
            totalValue += price * (log.quantity || 1);
          });
        } catch (e) {
          console.error("Error fetching prices:", e);
        }
      }
      
      // Count by delivery type from reference text
      const routeCount = todayLogs.filter(l => 
        l.reference?.toLowerCase().includes('runda') || 
        l.reference?.toLowerCase().includes('route')
      ).length;
      
      const storeCount = todayLogs.filter(l => 
        l.reference?.toLowerCase().includes('butik') || 
        l.reference?.toLowerCase().includes('store')
      ).length;
      
      setStats({
        pendingCount: pendingDeliveries.length,
        completedToday: todayLogs.length,
        totalValueToday: Math.round(totalValue),
        routePickups: routeCount,
        storeSales: storeCount
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const goToScan = () => {
    navigate('/scan');
  };

  const goToPickingList = (listId) => {
    navigate(`/picking/${listId}`);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString("sv-SE", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatPrice = (value) => {
    if (!value) return "—";
    return `${Math.round(value).toLocaleString('sv-SE')} kr`;
  };

  // Filter items based on search and filter type
  const filteredPending = pendingDeliveries.filter(item => {
    const matchesSearch = !searchQuery || 
      item.artikelnummer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.produktnamn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.ordernummer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.ean?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === "all" || 
      (filterType === "bring" && item.transportor === "Bring") ||
      (filterType === "dsv" && item.transportor === "DSV") ||
      (filterType === "other" && !["Bring", "DSV"].includes(item.transportor));
    
    return matchesSearch && matchesFilter;
  });

  const filteredCompleted = completedToday.filter(item => {
    const matchesSearch = !searchQuery || 
      item.artikelnummer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.produktnamn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.ean?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold" data-testid="driver-flow-title">
                  Lager & Leverans
                </h1>
                <p className="text-sm text-muted-foreground">
                  {user?.display_name || user?.username}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/ai-scan')} 
                className="gap-2"
                data-testid="scan-delivery-note-btn"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Skanna följesedel</span>
              </Button>
              <Button onClick={goToScan} className="gap-2" data-testid="go-to-scan-btn">
                <ScanBarcode className="h-4 w-4" />
                Skanna
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{stats.pendingCount}</p>
                <p className="text-xs text-muted-foreground">Att plocka</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.completedToday}</p>
                <p className="text-xs text-muted-foreground">Klart idag</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.routePickups}</p>
                <p className="text-xs text-muted-foreground">Till runda</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{formatPrice(stats.totalValueToday)}</p>
                <p className="text-xs text-muted-foreground">Värde idag</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="container mx-auto px-4 pb-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sök artikelnummer, order..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="driver-search-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla</SelectItem>
              <SelectItem value="bring">Bring</SelectItem>
              <SelectItem value="dsv">DSV</SelectItem>
              <SelectItem value="other">Övrigt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content Tabs */}
      <main className="container mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="pending" className="gap-2">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Att plocka</span>
              <Badge variant="secondary" className="ml-1">{filteredPending.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="completed" className="gap-2">
              <CheckCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Klart</span>
              <Badge variant="secondary" className="ml-1">{filteredCompleted.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="lists" className="gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Listor</span>
              <Badge variant="secondary" className="ml-1">{pickingLists.length}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Pending Items Tab */}
          <TabsContent value="pending" className="space-y-2">
            {filteredPending.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-bold text-lg">Allt är plockat!</h3>
                <p className="text-muted-foreground">Inga produkter att plocka just nu.</p>
              </Card>
            ) : (
              filteredPending.map((item, index) => (
                <Card 
                  key={`${item.listId}-${item.ean}-${index}`} 
                  className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => goToPickingList(item.listId)}
                  data-testid={`pending-item-${item.ean}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-sm truncate">{item.artikelnummer || item.ean}</span>
                        {item.transportor && (
                          <Badge variant={item.transportor === "Bring" ? "default" : "secondary"} className="text-xs">
                            {item.transportor}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{item.produktnamn || "—"}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {item.lagerplats || "—"}
                        </span>
                        {item.ordernummer && (
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {item.ordernummer}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-lg">{item.antal || 1}</p>
                        <p className="text-xs text-muted-foreground">st</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Completed Today Tab */}
          <TabsContent value="completed" className="space-y-2">
            {filteredCompleted.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-bold text-lg">Inga utleveranser idag</h3>
                <p className="text-muted-foreground">Börja skanna produkter för utleverans.</p>
                <Button onClick={goToScan} className="mt-4 gap-2">
                  <ScanBarcode className="h-4 w-4" />
                  Gå till skanning
                </Button>
              </Card>
            ) : (
              filteredCompleted.map((item, index) => (
                <Card key={item.id || index} className="p-3" data-testid={`completed-item-${item.ean}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-sm truncate">{item.artikelnummer || item.ean}</span>
                        {item.reference?.includes("Butik") && (
                          <Badge variant="outline" className="text-xs text-green-500 border-green-500">
                            <Store className="h-3 w-3 mr-1" />
                            Butik
                          </Badge>
                        )}
                        {item.reference?.includes("runda") && (
                          <Badge variant="outline" className="text-xs text-blue-500 border-blue-500">
                            <Truck className="h-3 w-3 mr-1" />
                            Runda
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{item.produktnamn || "—"}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(item.timestamp)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {item.location || "—"}
                        </span>
                        {item.username && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {item.username}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-red-500">-{item.quantity || 1}</p>
                      <p className="text-xs text-muted-foreground">st</p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Picking Lists Tab */}
          <TabsContent value="lists" className="space-y-2">
            {pickingLists.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-bold text-lg">Inga aktiva plocklistor</h3>
                <p className="text-muted-foreground">Importera en plocklista för att komma igång.</p>
                <Button onClick={() => navigate('/picking')} className="mt-4 gap-2">
                  <ArrowRight className="h-4 w-4" />
                  Gå till plocklistor
                </Button>
              </Card>
            ) : (
              pickingLists.map((list) => {
                const totalItems = list.items?.length || 0;
                const pickedItems = list.items?.filter(i => i.picked).length || 0;
                const progress = totalItems > 0 ? Math.round((pickedItems / totalItems) * 100) : 0;
                
                return (
                  <Card 
                    key={list.id} 
                    className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => goToPickingList(list.id)}
                    data-testid={`picking-list-${list.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold">{list.name}</h3>
                      <Badge variant={progress === 100 ? "default" : "secondary"}>
                        {progress}%
                      </Badge>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 mb-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{pickedItems} av {totalItems} plockade</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(list.created_at).toLocaleDateString("sv-SE")}
                      </span>
                    </div>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Quick Action FAB for mobile */}
      <div className="fixed bottom-20 right-4 md:hidden">
        <Button 
          size="lg" 
          className="rounded-full w-14 h-14 shadow-lg"
          onClick={goToScan}
        >
          <ScanBarcode className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
}
